import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SignupPageWall from '../assets/images/SignupPageWall.png';
import Google from '../assets/images/google.png';
import GreenTick from '../assets/images/greenTick.png';
import { Button } from '../components/Button';
import { SizeEnum, validateEmail } from '../utils';
import FormInput from '../components/FormInput';
import AuthPage from '../components/AuthPage';
import { useSignUpEmailPassword, useNhostClient } from '@nhost/react';

import { Text } from 'components/Text';
import classNames from 'classnames';
import { SingInSignUpBanner } from 'components/SingInSignUpBanner';
import ReCAPTCHA from 'react-google-recaptcha';

const SHOPIFY_INSTALL_RETURN_KEY = 'shipaid___post-install';
const KLAVIYO_INSTALL_RETURN_KEY = 'shipaid___klaviyo-install-pending';

function getSafeInstallReturnPath(value: string | null) {
  if (!value) return null;
  try {
    const destination = new URL(value, window.location.origin);
    return (destination.protocol === 'http:' ||
      destination.protocol === 'https:') &&
      destination.origin === window.location.origin
      ? value
      : null;
  } catch {
    return null;
  }
}

function SignUp() {
  const nhost = useNhostClient();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [refCode, setRefCode] = useState(searchParams.get('ref') || '');

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const handleRecaptcha = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const [errorObj, setErrorObj] = useState({
    message: '',
    error: '',
    status: 0,
  });
  const {
    isLoading,
    isSuccess,
    needsEmailVerification,
    error,
    signUpEmailPassword,
  } = useSignUpEmailPassword();

  useEffect(() => {
    if (error) {
      setErrorObj({ ...error });
    }
  }, [error]);

  function handleValidation() {
    if (email.trim() === '') {
      const error = { message: 'Email is required', error: 'invalid-email' };
      setErrorObj({ ...errorObj, ...error });
      return error;
    }
    if (!validateEmail(email)) {
      const error = {
        message: 'Email is not valid, please enter a valid email',
        error: 'invalid-email',
      };
      setErrorObj({ ...errorObj, ...error });
      return error;
    }
    if (password.trim() === '') {
      const error = {
        message: 'Password is required',
        error: 'invalid-password',
      };
      setErrorObj({ ...errorObj, ...error });
      return error;
    }
    setErrorObj({ message: '', error: '', status: 0 });
    return false;
  }

  function handleSignup(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const hasError = handleValidation();

    if (hasError) return;

    if (!recaptchaToken) {
      setErrorObj({
        message: 'Please verify the reCAPTCHA.',
        error: 'recaptcha-missing',
        status: 400,
      });
      return;
    }
    if (!hasError)
      signUpEmailPassword(email, password, {
        metadata: {
          ref_id: refCode,
        },
      });
  }

  const shopifySignupURI = getSafeInstallReturnPath(
    localStorage.getItem(SHOPIFY_INSTALL_RETURN_KEY),
  );
  // Set by KlaviyoMarketplaceInstall.tsx when a merchant with no ShipAid account yet clicks
  // Install from Klaviyo's Marketplace — mirrors Login.tsx's identical check, since a brand-new
  // merchant hits signup, not login, and needs the same resume-after-auth behavior. Without this,
  // Klaviyo's documented "user has no account -> creates one -> redirected to the OAuth
  // installation flow" case dead-ends at a normal post-signup page instead.
  const klaviyoInstallReturnPath = getSafeInstallReturnPath(
    localStorage.getItem(KLAVIYO_INSTALL_RETURN_KEY),
  );

  function handleSignupWithGoogle() {
    nhost.auth.signIn({
      provider: 'google',
      options: {
        redirectTo: shopifySignupURI || klaviyoInstallReturnPath || '/',
      },
    });
  }

  const handledSignupSuccess = useRef(false);
  useEffect(() => {
    if (!isSuccess) {
      handledSignupSuccess.current = false;
      return;
    }
    if (handledSignupSuccess.current) return;
    handledSignupSuccess.current = true;

    const shopifyReturnPath = getSafeInstallReturnPath(
      localStorage.getItem(SHOPIFY_INSTALL_RETURN_KEY),
    );
    const klaviyoReturnPath = getSafeInstallReturnPath(
      localStorage.getItem(KLAVIYO_INSTALL_RETURN_KEY),
    );
    localStorage.removeItem(SHOPIFY_INSTALL_RETURN_KEY);
    localStorage.removeItem(KLAVIYO_INSTALL_RETURN_KEY);
    if (shopifyReturnPath) {
      window.location.href = shopifyReturnPath;
    } else if (klaviyoReturnPath) {
      navigate(klaviyoReturnPath);
    } else {
      navigate('/');
    }
  }, [isSuccess, navigate]);

  const disableForm = isLoading || needsEmailVerification;

  const handleRefCodeChange = (event: any) => {
    const value = event.target.value;
    setRefCode(value);
    searchParams.set('ref', value);
    navigate(`?${searchParams.toString()}`);
  };

  return (
    <AuthPage backgroundImage={SignupPageWall}>
      <div className="text-center my-6 w-11/12">
        <h6 className="uppercase text-shipaidBlue my-4 font-bold">Account</h6>
        {searchParams.get('shop-name') && (
          <SingInSignUpBanner store={searchParams.get('shop-name') || ''} />
        )}
        <h2 className="text-3xl md:text-5xl my-4">Welcome!</h2>

        {shopifySignupURI && !searchParams.get('shop-name') ? (
          <>
            <div className="flex items-center max-w-4xl">
              <img className="h-6 w-6 mr-3 ml-10" src={GreenTick} alt="tick" />
              Thank you for installing the ShipAid Shopify App!
            </div>
            <div>
              Please signup below to finish setup and continue managing your
              account.
            </div>
          </>
        ) : null}

        <div className="text-sm md:text-xl">
          <span>Already have an account? </span>
          <span className="text-shipaidBlue">
            <Link to="/login">Login</Link>
          </span>
        </div>

        <Button
          size={SizeEnum.sm}
          isRounded
          onClick={handleSignupWithGoogle}
          className="bg-transparent border border-black w-full rounded-full flex justify-center items-center align-middle text-center mt-4"
        >
          <img className="h-6 w-6" src={Google} alt="google logo" />
          <span className="block pl-2"> Sign up with google</span>
        </Button>

        <div className="my-4">
          <fieldset className="border-t border-slate-300">
            <legend className="mx-auto px-2 text-black text-sm">
              Or Continue with
            </legend>
          </fieldset>
        </div>
      </div>

      <div className="form w-full  md:w-[450px]">
        <form className="w-full" onSubmit={handleSignup}>
          <div className="mb-4">
            <FormInput
              id="email"
              name="email"
              label="Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="franklyfrank@example.com"
              type="email"
            />
          </div>

          <div className="mb-4">
            <FormInput
              id="password"
              name="password"
              label="Your Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Better be a good one"
              type="password"
            />
          </div>
          <div className="mb-4 w-1/2">
            <FormInput
              id="ref-code"
              name="refCode"
              label="Code"
              value={refCode}
              onChange={handleRefCodeChange}
              placeholder="Referral Code (Optional)"
              type="text"
            />
          </div>
          <Text
            type="sm"
            value={errorObj?.message}
            className={classNames('text-center text-red-600')}
          />
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
            onChange={handleRecaptcha}
          />

          <Button
            size={SizeEnum.xl}
            isRounded
            type="submit"
            disabled={disableForm}
            className="w-full rounded-full flex justify-center items-center align-middle text-center mt-4 hover:bg-opacity-80"
          >
            Submit
          </Button>
        </form>
      </div>
    </AuthPage>
  );
}

export default SignUp;
